/**
 * View-model helpers for the Action Pending tab (inclusion accordions + CRC cards).
 */

import type {
  AtomScreeningAnswer,
  PatientScreeningResult,
  ScreeningAnswer,
} from "../../../../api/clinicalTrialsApiTypes";
import type { NoteAssigneeResponse } from "../../../../api/clinicalTrialsApiTypes";
import type {
  CreateEligibilityTaskRequest,
  EligibilityRecommendedAction,
} from "../../../../data-model/eligibilityDetail";
import { questionDisplayText } from "../../shared/screeningStatusDisplay";

/** One inclusion-criteria accordion block on the left pane. */
export interface InclusionCriteriaGroup {
  /** Stable key for React lists (question + atom). */
  id: string;
  title: string;
  points: string[];
}

/** Display fields for {@link CrcActionCard}. */
export interface CrcActionCardViewModel {
  actionId: string;
  atomId: string;
  title: string;
  subtitle: string;
  meta: string;
}

/**
 * Locate a screening question across all stages.
 *
 * @param screening - Latest screening payload from detail
 * @param questionId - Protocol question id
 * @returns Matching question, if present
 */
function findScreeningQuestion(
  screening: PatientScreeningResult | null | undefined,
  questionId: string,
): ScreeningAnswer | undefined {
  if (!screening?.stages) {
    return undefined;
  }
  for (const stage of screening.stages) {
    const match = stage.questions?.find(
      (question) => question.question_id === questionId,
    );
    if (match) {
      return match;
    }
  }
  return undefined;
}

/**
 * Locate one atom on a screening question.
 *
 * @param question - Parent question
 * @param atomId - Protocol atom id
 * @returns Atom answer, if present
 */
function findScreeningAtom(
  question: ScreeningAnswer | undefined,
  atomId: string,
): AtomScreeningAnswer | undefined {
  return question?.atoms?.find((atom) => atom.atom_id === atomId);
}

/**
 * Resolve atom context for a recommended action from the latest screening run.
 *
 * @param screening - Latest screening payload
 * @param action - Recommended action row
 * @returns Question and atom screening answers when found
 */
function resolveActionScreeningContext(
  screening: PatientScreeningResult | null | undefined,
  action: EligibilityRecommendedAction,
): {
  question: ScreeningAnswer | undefined;
  atom: AtomScreeningAnswer | undefined;
} {
  const question = findScreeningQuestion(screening, action.question_id);
  const atom = findScreeningAtom(question, action.atom_id);
  return { question, atom };
}

/**
 * Human-readable accordion title from a protocol question id.
 *
 * @param questionId - Raw question id from screening
 * @returns Display title for the accordion header
 */
function formatQuestionTitle(questionId: string): string {
  if (/^Q\d+/i.test(questionId)) {
    return questionId;
  }
  return questionId
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Meta line for a CRC action card from atom status and deciding fact.
 *
 * @param action - Recommended action row
 * @param atom - Matching atom screening answer
 * @returns Italic meta copy under the action description
 */
function formatActionMeta(
  action: EligibilityRecommendedAction,
  atom: AtomScreeningAnswer | undefined,
): string {
  if (atom?.deciding_fact?.trim()) {
    return atom.deciding_fact.trim();
  }
  if (
    action.status === "DATA_MISSING" ||
    action.status === "FAILED_TODAY_BUT_CAN_BE_RECHECKED_IN_FUTURE"
  ) {
    return "Result pending";
  }
  if (action.status === "ACTION_REQUIRED") {
    return "Action required";
  }
  return `Status: ${action.status}`;
}

/**
 * Primary card title from the recommended action text.
 *
 * @param action - Recommended action row
 * @returns Short label for the card header
 */
function formatActionTitle(action: EligibilityRecommendedAction): string {
  const firstSegment = action.action.split(/[.(\n]/)[0]?.trim();
  if (firstSegment) {
    return firstSegment;
  }
  return action.atom_id || action.question_id;
}

/**
 * Build inclusion accordion groups from screening + recommended actions.
 *
 * Multiple recommended actions for the same protocol question share one accordion;
 * expanding it lists every distinct criterion text for that question.
 *
 * @param screening - `detail.latest_screening`
 * @param actions - `detail.recommended_actions`
 * @returns Left-column accordion data (one group per distinct question)
 */
export function buildInclusionCriteriaGroups(
  screening: PatientScreeningResult | null | undefined,
  actions: EligibilityRecommendedAction[],
): InclusionCriteriaGroup[] {
  const groupsByKey = new Map<string, InclusionCriteriaGroup>();
  const orderedKeys: string[] = [];

  for (const action of actions) {
    const { question, atom } = resolveActionScreeningContext(screening, action);
    const title =
      (question ? questionDisplayText(question) : "") ||
      formatQuestionTitle(action.question_id);
    const groupKey = action.question_id;

    const newPoints: string[] = [];
    if (question?.question_verbatim_text?.trim()) {
      newPoints.push(question.question_verbatim_text.trim());
    } else if (question?.reasoning?.trim()) {
      newPoints.push(question.reasoning.trim());
    }
    if (atom?.deciding_fact?.trim()) {
      const fact = atom.deciding_fact.trim();
      if (!newPoints.includes(fact)) {
        newPoints.push(fact);
      }
    }
    if (newPoints.length === 0) {
      newPoints.push(action.action);
    }

    const existing = groupsByKey.get(groupKey);
    if (existing) {
      for (const point of newPoints) {
        if (!existing.points.includes(point)) {
          existing.points.push(point);
        }
      }
      continue;
    }

    orderedKeys.push(groupKey);
    groupsByKey.set(groupKey, {
      id: groupKey,
      title,
      points: [...newPoints],
    });
  }

  return orderedKeys.map((key) => groupsByKey.get(key)!);
}

/**
 * Map a recommended action into CRC action card copy.
 *
 * @param action - Server recommended action row
 * @param screening - Latest screening for deciding-fact meta
 * @returns Card title, subtitle, and meta line
 */
export function mapRecommendedActionToCard(
  action: EligibilityRecommendedAction,
  screening: PatientScreeningResult | null | undefined,
): CrcActionCardViewModel {
  const { atom } = resolveActionScreeningContext(screening, action);

  return {
    actionId: action.action_id,
    atomId: action.atom_id,
    title: formatActionTitle(action),
    subtitle: action.action,
    meta: formatActionMeta(action, atom),
  };
}

/**
 * Format a local calendar date as `YYYY-MM-DD` for the create-task API.
 *
 * @param date - Calendar date in local time
 * @returns ISO date string
 */
function toIsoCalendarDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Default task due date: thirty calendar days from today (local).
 *
 * @returns ISO due date for create-task
 */
function defaultTaskDueDate(): string {
  const due = new Date();
  due.setDate(due.getDate() + 30);
  return toIsoCalendarDate(due);
}

/**
 * Note body copy for create-task from the server-owned recommendation text.
 *
 * @param recommendation - Full recommended action string from screening
 * @returns CRC-prefixed task body
 */
function formatTaskBody(recommendation: string): string {
  const trimmed = recommendation.trim();
  if (/^CRC\b/i.test(trimmed)) {
    return trimmed.endsWith(".") ? trimmed : `${trimmed}.`;
  }
  const withoutPeriod = trimmed.replace(/\.\s*$/, "");
  const normalized =
    withoutPeriod.length > 0
      ? withoutPeriod.charAt(0).toLowerCase() + withoutPeriod.slice(1)
      : withoutPeriod;
  return `CRC to ${normalized}.`;
}

/**
 * Pick the assignee for a create-task command from the facility directory.
 *
 * @param assignees - Response from `GET /notes/assignees`
 * @returns Current user's id when flagged, otherwise the first eligible member
 */
export function resolveTaskAssigneeId(
  assignees: NoteAssigneeResponse[],
): string | null {
  const currentUser = assignees.find((member) => member.is_current_user);
  if (currentUser?.user_id) {
    return currentUser.user_id;
  }
  return assignees[0]?.user_id ?? null;
}

/**
 * Build the JSON body for `POST .../recommended-actions/{action_id}/task`.
 *
 * @param action - Checked recommended action row
 * @param assigneeId - Facility member id from the assignees directory
 * @returns Create-task request matching the eligibility command contract
 */
export function buildCreateEligibilityTaskRequest(
  action: EligibilityRecommendedAction,
  assigneeId: string | null,
): CreateEligibilityTaskRequest {
  return {
    assignee_id: assigneeId,
    due_date: defaultTaskDueDate(),
    title: formatActionTitle(action),
    body: formatTaskBody(action.action),
  };
}
